import React from "react";
import { FeatureAtlas, LandingFAQ, FAQ } from "@/components/FeatureAtlas";
import { useSeo } from "@/lib/useSeo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Scale, Globe2, ShieldCheck, FileSearch, GitCompare,
  MessagesSquare, Building2, Check, Lock, Cpu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { ChatWidget } from "@/components/ChatWidget";
import { Reveal } from "@/components/Reveal";

const JURIS = [
  ["🇫🇮", "Finland"], ["🇺🇸", "United States"], ["🇬🇧", "United Kingdom"],
  ["🇩🇪", "Germany"], ["🇸🇪", "Sweden"], ["🇳🇴", "Norway"],
  ["🇩🇰", "Denmark"], ["🇫🇷", "France"], ["🇨🇦", "Canada"], ["🇦🇺", "Australia"],
];

const AGENTS = [
  { icon: FileSearch, title: "Legal Research Agent", desc: "Finds relevant statutes, regulations and authorities scoped to a single jurisdiction." },
  { icon: GitCompare, title: "Comparison Agent", desc: "Produces structured, side-by-side comparisons across two jurisdictions." },
  { icon: ShieldCheck, title: "Compliance Agent", desc: "Surfaces compliance risks and obligations for your operating regions." },
  { icon: Scale, title: "Document Analysis Agent", desc: "Reviews contracts for risks, missing clauses and key obligations." },
  { icon: MessagesSquare, title: "Summary Agent", desc: "Translates dense legal language into clear, plain explanations." },
];

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const LANDING_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
};

export default function Landing() {
  useSeo({ title: "Legalis AI | Private Legal Intelligence Across 13 Jurisdictions", path: "/", jsonLd: LANDING_LD,
    description: "AI legal intelligence for founders, family offices and law firms: cited answers grounded in official statutes and live research, deep research memos, triage, strategy, documents and deadline tracking." });
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[hsl(250_14%_2.4%)]/75 backdrop-blur-2xl border-b border-white/[0.05]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-9 text-[13px] text-muted-foreground">
            <a href="#platform" className="hover:text-foreground transition-colors" data-testid="nav-platform">Platform</a>
            <a href="#features" className="hover:text-foreground transition-colors" data-testid="nav-features">Features</a>
            <a href="#agents" className="hover:text-foreground transition-colors" data-testid="nav-agents">Intelligence</a>
            <a href="#jurisdictions" className="hover:text-foreground transition-colors" data-testid="nav-jurisdictions">Jurisdictions</a>
            <a href="#pricing" className="hover:text-foreground transition-colors" data-testid="nav-pricing">Pricing</a>
            <Link to="/for/startups" className="hover:text-foreground transition-colors" data-testid="nav-solutions">For startups</Link>
          </nav>
          <div className="flex items-center gap-1 sm:gap-2">
            <Link to="/login"><Button variant="ghost" className="px-3 sm:px-4" data-testid="header-login-btn">Sign in</Button></Link>
            <Link to="/register"><Button className="px-3 sm:px-4" data-testid="header-register-btn"><span className="sm:hidden">Join</span><span className="hidden sm:inline">Request access</span></Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 lg:px-8 pt-6">
        <div className="relative max-w-[1400px] mx-auto rounded-[28px] overflow-hidden border border-white/[0.06] bg-[hsl(250_14%_2%)] noise min-h-[640px] lg:min-h-[720px]">
          <motion.img src="/hero-monolith.jpg" alt="" aria-hidden
            initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.6, ease: [0.2, 0.7, 0.3, 1] }}
            className="absolute inset-0 w-full h-full object-cover object-[70%_center]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(250_14%_2%)] via-[hsl(250_14%_2%)]/85 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[hsl(250_14%_2%)] to-transparent" />
          <div className="relative z-10 px-8 lg:px-20 pt-24 lg:pt-32 pb-16 max-w-4xl">
            <motion.div variants={fade} initial="hidden" animate="show">
              <div className="overline text-brand mb-7 inline-flex items-center gap-3"><span className="w-8 h-px bg-brand/60" /> Private legal intelligence · 13 jurisdictions</div>
              <h1 className="text-display text-5xl sm:text-6xl lg:text-[76px] text-[hsl(240_12%_96%)]">
                Law, made clear.<br /><span className="text-[hsl(var(--gold-bright))]">Matters,</span> in command.
              </h1>
              <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
                Legalis AI is the intelligence layer for counsel, founders and family offices: every answer grounded in official statutes and live, cited research, across every jurisdiction you operate in.
              </p>
            </motion.div>
            <motion.div variants={fade} custom={1} initial="hidden" animate="show" className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/register">
                <Button size="lg" className="group h-12 px-8" data-testid="hero-cta-btn">
                  Request access <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/login"><Button size="lg" variant="outline" className="h-12 px-8" data-testid="hero-demo-btn">Explore the platform</Button></Link>
            </motion.div>
            <motion.div variants={fade} custom={2} initial="hidden" animate="show" className="mt-14 flex flex-wrap gap-x-8 gap-y-3 text-[13px] text-muted-foreground">
              <span className="flex items-center gap-2"><Lock className="w-4 h-4 text-brand" /> End-to-end encrypted</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brand" /> Official statute feeds</span>
              <span className="flex items-center gap-2"><Cpu className="w-4 h-4 text-brand" /> Live cited web research</span>
            </motion.div>
          </div>
          <div className="hidden xl:flex absolute right-16 top-1/2 -translate-y-1/2 items-stretch gap-6 z-10">
            <div className="hairline-v" />
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-muted-foreground leading-[2.4]">Legal<br />Intelligence<br />Since 2016</div>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto mt-6 px-2 flex flex-wrap items-center justify-between gap-4 text-[12px] text-muted-foreground">
          <span>Built with <span className="text-foreground font-semibold">Google</span> &amp; <span className="text-foreground font-semibold">Anthropic</span> foundations</span>
          <span className="font-mono tracking-[0.2em] uppercase text-[10px]">FI · US · UK · EU · NO · SE · DK · DE · FR · CA · AU</span>
        </div>
      </section>

      {/* Platform / Features */}
      <section id="platform" className="max-w-7xl mx-auto px-6 py-20 md:py-24">
        <div className="overline text-brand mb-3">The platform</div>
        <h2 className="font-serif text-3xl md:text-4xl font-semibold max-w-2xl">Built for global legal complexity, not chat.</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-4">
          <Feature className="md:col-span-7" icon={MessagesSquare} title="Jurisdiction-aware AI assistant"
            desc="Select a country or state, then ask naturally. Every answer is scoped to one jurisdiction and returns key points, cited sources and professional-advice warnings." />
          <Feature className="md:col-span-5" icon={GitCompare} title="Cross-jurisdiction comparison"
            desc="Compare a legal topic between two jurisdictions in a structured, tabular view — differences, similarities and business impact." />
          <Feature className="md:col-span-5" icon={FileSearch} title="Document analysis"
            desc="Upload contracts and policies. Get risks, missing clauses, key obligations and a plain-language summary." />
          <Feature className="md:col-span-7" icon={Building2} title="Multi-tenant SaaS & teams"
            desc="Each organization gets isolated data, team seats, roles and permissions, usage tracking and audit logs — ready for enterprise." />
        </div>
      </section>

      {/* Agents */}
      <section id="agents" className="border-y border-border bg-secondary/40">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">
          <div className="overline text-brand mb-3">Modular AI agents</div>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold max-w-2xl">Five specialized agents, one architecture.</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {AGENTS.map((a, i) => (
              <div key={i} className="lux-card sheen p-7">
                <a.icon className="w-6 h-6 text-brand mb-4" strokeWidth={1.75} />
                <h3 className="font-serif text-lg font-semibold">{a.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Jurisdictions */}
      <section id="jurisdictions" className="max-w-7xl mx-auto px-6 py-20 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <Globe2 className="w-7 h-7 text-brand mb-4" />
            <h2 className="font-serif text-3xl md:text-4xl font-semibold">Ten countries. Federal &amp; state.</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              A scalable jurisdiction architecture. Add new countries and sub-national
              jurisdictions without rebuilding the application.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {JURIS.map(([flag, name]) => (
              <div key={name} className="border border-border bg-card px-4 py-3 flex items-center gap-3">
                <span className="text-xl">{flag}</span>
                <span className="text-sm font-medium">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FeatureAtlas />

      {/* Pricing */}
      <section id="pricing" className="border-t border-border bg-secondary/40">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">
          <div className="overline text-brand mb-3">Pricing</div>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold">From free to global enterprise.</h2>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">Seven tiers — a free consumer plan, plans for professionals, startups and SMEs, plus dedicated Law Firm and Enterprise tiers. Enterprise includes our AI-mistake warranty; it's a $30,000/mo add-on on other paid plans.</p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: "Free", price: "$0", per: "", desc: "Consumers", feats: ["20 AI questions / mo", "All 13 jurisdictions", "Cited, streaming answers", "2 document analyses"] },
              { name: "Professional", price: "$299", per: "/mo", desc: "Small teams", highlight: true, feats: ["1,500 AI questions / mo", "Up to 8 seats", "Knowledge base (RAG)", "Roles & audit logging"] },
              { name: "Business", price: "$10,000", per: "/mo", desc: "SMEs & companies", feats: ["30,000 AI questions / mo", "100 seats", "SAML / OIDC SSO", "Law-firm referral network"] },
              { name: "Enterprise", price: "$99,000", per: "/mo", desc: "Law firms & global", feats: ["Unlimited AI & seats", "AI-mistake warranty included", "Custom model routing", "Private deployment"] },
            ].map((p) => (
              <div key={p.name} className={`border bg-card p-7 flex flex-col ${p.highlight ? "border-brand" : "border-border"}`}>
                {p.highlight && <div className="overline text-brand mb-3">Most popular</div>}
                <h3 className="font-serif text-xl font-semibold">{p.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{p.desc}</p>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="font-serif text-4xl font-semibold">{p.price}</span>
                  <span className="text-muted-foreground text-sm">{p.per}</span>
                </div>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.feats.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-brand mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/register" className="mt-7">
                  <Button className="w-full" variant={p.highlight ? "default" : "outline"} data-testid={`pricing-${p.name.toLowerCase()}-btn`}>
                    {p.name === "Free" ? "Start free" : "Get started"}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">Also available: Starter ($99/mo), Startup ($500/mo) and Law Firm ($30,000/mo) plans.</p>
        </div>
      </section>

      <LandingFAQ />

      {/* CTA + Footer */}
      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <Logo />
            <p className="text-sm text-muted-foreground mt-3 max-w-md">
              AI-generated legal information, not legal advice. Always consult a qualified attorney.
            </p>
          </div>
          <nav className="flex flex-wrap gap-5 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground">Features</a><a href="#pricing" className="hover:text-foreground">Pricing</a>
            <a href="#faq" className="hover:text-foreground">FAQ</a><Link to="/insights" className="hover:text-foreground" data-testid="footer-insights">Insights</Link><Link to="/privacy" className="hover:text-foreground" data-testid="footer-privacy">Privacy</Link><Link to="/delete-account" className="hover:text-foreground" data-testid="footer-delete-account">Delete account</Link>
          </nav>
          <div className="text-sm text-muted-foreground">© 2026 Legalis AI. In collaboration with Google &amp; Anthropic · Since 2016.</div>
        </div>
      </footer>
      <ChatWidget mode="landing" />
    </div>
  );
}

function Feature({ icon: Icon, title, desc, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, ease: [0.2, 0.7, 0.3, 1] }}
      className={`lux-card sheen p-8 ${className}`}>
      <Icon className="w-6 h-6 text-brand mb-4" strokeWidth={1.75} />
      <h3 className="font-serif text-xl font-semibold">{title}</h3>
      <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-lg">{desc}</p>
    </motion.div>
  );
}
